import { Check } from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { wrappedIndex } from "@/lib/wrapped-index";
import { Button } from "./button";
import { Input } from "./input";

/**
 * The one list of choices every such list on the screen shares, in two parts.
 *
 * `Listbox` is the list alone: the options, the row in hand and the letters that matched. It
 * is driven by an active index and a choose act, so a list whose keys come from elsewhere uses
 * it on its own, such as a list opened at the caret in an editor that keeps the focus and
 * sends the keys itself.
 *
 * `useCombobox` and `TypeAhead` are the box over it: they own an input and its keys, and draw
 * the list with `Listbox`. The focus stays in the box and the arrows move which option is in
 * hand; Enter or a press takes it. `TypeAhead` draws it under a field that can also add what
 * was typed; `Switcher` draws it in a popover that swaps the thing a page is about.
 */

/** One thing a list offers: what it is called, and a muted line saying whose it is. */
export interface ComboboxOption {
  key: string;
  label: string;
  /** A muted word beside the label, such as the client an inventor is on record for. */
  detail?: string;
  /** Whether it is the one already chosen, which carries a tick. */
  current?: boolean;
}

/**
 * The row in hand after one step: the arrows wrap, and from none (-1) down takes the first and
 * up the last. An empty list keeps none.
 */
export function moveActive(at: number, step: 1 | -1, count: number): number {
  if (count === 0) return -1;
  if (at < 0) return step > 0 ? 0 : count - 1;
  return wrappedIndex(at, step, count);
}

/** The label with the first stretch that matched what was typed marked, whatever its case. */
function Marked({ label, match }: { label: string; match?: string }) {
  const typed = match?.trim() ?? "";
  const at =
    typed === "" ? -1 : label.toLowerCase().indexOf(typed.toLowerCase());
  if (at < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, at)}
      <mark className="bg-transparent font-bold text-inherit">
        {label.slice(at, at + typed.length)}
      </mark>
      {label.slice(at + typed.length)}
    </>
  );
}

/** One row of a list: the label with its matched letters marked, its muted detail, its tick. */
function ListboxRow<T extends ComboboxOption>({
  id,
  option,
  inHand,
  match,
  onHover,
  onTake,
}: {
  id: string;
  option: T;
  inHand: boolean;
  match?: string;
  onHover: () => void;
  onTake: (option: T) => void;
}) {
  return (
    <li
      id={id}
      role="option"
      aria-selected={inHand}
      // Named whole, since the tick says it only to the eye.
      aria-label={option.current ? `${option.label} (current)` : undefined}
      // Pressing keeps the focus where it is, so the list does not shut under the press
      // before it lands.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onMouseEnter={onHover}
      onClick={() => {
        onTake(option);
      }}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-sm px-[9px] py-[7px] text-[12px] leading-[1.3] font-semibold",
        inHand && "bg-muted text-primary",
      )}
    >
      <span className="min-w-0 flex-1 truncate">
        <Marked label={option.label} match={match} />
      </span>
      {option.detail !== undefined && (
        <span className="shrink-0 font-normal text-muted-foreground">
          {option.detail}
        </span>
      )}
      {option.current && (
        <Check aria-hidden className="size-[13px] shrink-0 text-primary" />
      )}
    </li>
  );
}

/** What a list holds, or a line saying what to do when it holds nothing. */
export function Listbox<T extends ComboboxOption>({
  listId,
  name,
  options,
  active,
  optionId,
  onHover,
  onTake,
  empty,
  match,
}: {
  listId: string;
  /** The listbox's name, as a reader hears it. */
  name: string;
  options: readonly T[];
  active: number;
  optionId: (at: number) => string;
  onHover: (at: number) => void;
  onTake: (option: T) => void;
  empty: ReactNode;
  /** What was typed, whose letters each label marks where they match. */
  match?: string;
}) {
  if (options.length === 0) {
    return (
      <p className="px-[9px] py-[7px] text-[12px] text-muted-foreground">
        {empty}
      </p>
    );
  }
  return (
    <ul
      id={listId}
      role="listbox"
      aria-label={name}
      className="max-h-[280px] overflow-y-auto"
    >
      {options.map((option, at) => (
        <ListboxRow
          key={option.key}
          id={optionId(at)}
          option={option}
          inHand={at === active}
          match={match}
          onHover={() => {
            onHover(at);
          }}
          onTake={onTake}
        />
      ))}
    </ul>
  );
}

/**
 * The keyboard and the ids a box over a list needs, built on `moveActive`.
 *
 * `initial` is the option in hand when the list opens: the current one in a switcher, or none
 * (-1) in a field where Enter on its own means what was typed.
 */
export function useCombobox<T extends ComboboxOption>({
  options,
  initial,
  onTake,
  onEscape,
}: {
  options: readonly T[];
  initial: number;
  onTake: (option: T) => void;
  onEscape?: () => void;
}) {
  const [active, setActive] = useState(initial);
  const id = useId();
  const optionId = (at: number) => `${id}-option-${String(at)}`;
  const inHand =
    active >= 0 && active < options.length ? options[active] : undefined;
  const inHandId = inHand === undefined ? undefined : optionId(active);
  // The list scrolls past a few dozen; keep the one in hand where it can be seen.
  useEffect(() => {
    if (inHandId === undefined) return;
    document.getElementById(inHandId)?.scrollIntoView({ block: "nearest" });
  }, [inHandId]);

  /** Answers whether the key was the box's, so a caller can give Enter a meaning of its own. */
  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): boolean {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((at) => moveActive(at, step, options.length));
      return true;
    }
    if (event.key === "Enter" && inHand !== undefined) {
      event.preventDefault();
      onTake(inHand);
      return true;
    }
    if (event.key === "Escape" && onEscape !== undefined) {
      event.preventDefault();
      onEscape();
      return true;
    }
    return false;
  }

  return {
    active,
    setActive,
    optionId,
    onKeyDown,
    listId: `${id}-list`,
    /** Spread on the input, with `aria-expanded` and the rest of its own props. */
    inputProps: {
      role: "combobox" as const,
      "aria-controls": `${id}-list`,
      "aria-activedescendant": inHandId,
      "aria-autocomplete": "list" as const,
    },
  };
}

const LIST_PANEL =
  "absolute top-[calc(100%+6px)] left-0 z-40 flex w-full flex-col gap-1 rounded-menu border border-border bg-card p-[5px] text-left font-normal text-foreground shadow-popover";

/** The list under a field, with the line that says how to add a name it does not hold. */
function ListUnderField<T extends ComboboxOption>({
  combo,
  options,
  listName,
  typed,
  onTake,
}: {
  combo: ReturnType<typeof useCombobox<T>>;
  options: readonly T[];
  listName: string;
  typed: string;
  onTake: (option: T) => void;
}) {
  return (
    <div className={LIST_PANEL}>
      <Listbox
        listId={combo.listId}
        name={listName}
        options={options}
        active={combo.active}
        optionId={combo.optionId}
        onHover={combo.setActive}
        onTake={onTake}
        empty={`Press Add to use "${typed}".`}
      />
    </div>
  );
}

/** Add, beside the field, for a name nothing on record is; closed while there is nothing to add. */
function AddAct({
  name,
  disabled,
  onAdd,
}: {
  name: string;
  disabled: boolean;
  onAdd: () => void;
}) {
  return (
    <Button
      type="button"
      variant="way"
      size="field"
      aria-label={name}
      disabled={disabled}
      onClick={onAdd}
    >
      Add
    </Button>
  );
}

/**
 * A key in the field. The list's keys act only while it shows; an arrow opens a closed list,
 * and Enter adds what was typed rather than submitting the form the field sits in.
 */
function fieldKey(
  event: KeyboardEvent<HTMLInputElement>,
  showing: boolean,
  acts: {
    list: (event: KeyboardEvent<HTMLInputElement>) => boolean;
    open: () => void;
    add: () => void;
  },
) {
  if (showing && acts.list(event)) return;
  if (!showing && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
    event.preventDefault();
    acts.open();
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    acts.add();
  }
}

/** How many characters a field waits for before it offers anything. */
const FROM = 2;

/** What a type-ahead is given: the field's words, what is on record, and what each act does. */
export interface TypeAheadProps<T extends ComboboxOption> {
  inputId: string;
  value: string;
  onValue: (value: string) => void;
  options: readonly T[];
  listName: string;
  onChoose: (option: T) => void;
  onAdd: (text: string) => void;
  /** The Add act's name, as a reader hears it: "Add inventor". */
  addName: string;
  describedBy?: string;
  placeholder?: string;
  /**
   * The answer for the words as they now read is still on its way. The list then holds back
   * the line that says how to add, which is only for words nothing on record matches.
   */
  pending?: boolean;
}

/**
 * A field that offers what is already on record as the attorney types, and adds a new name
 * when nothing offered is it.
 *
 * The list opens from two characters. Nothing is in hand until an arrow moves, so Enter on its
 * own adds what was typed; with an option in hand it chooses that one. Escape, or the focus
 * going elsewhere, closes the list, keeps the words and lets go of the row in hand; an arrow
 * opens the list again.
 */
export function TypeAhead<T extends ComboboxOption>({
  inputId,
  value,
  onValue,
  options,
  listName,
  onChoose,
  onAdd,
  addName,
  describedBy,
  placeholder,
  pending = false,
}: TypeAheadProps<T>) {
  const [open, setOpen] = useState(false);
  const typed = value.trim();
  const showing =
    open && typed.length >= FROM && (options.length > 0 || !pending);
  function close() {
    setOpen(false);
    combo.setActive(-1);
  }
  function take(option: T) {
    close();
    onChoose(option);
  }
  const combo = useCombobox({
    options,
    initial: -1,
    onTake: take,
    onEscape: close,
  });

  function add() {
    if (typed === "") return;
    close();
    onAdd(typed);
  }

  return (
    <div className="relative flex gap-2">
      <Input
        id={inputId}
        size="roomy"
        {...combo.inputProps}
        // Only a row the reader can reach is named; a closed list holds none.
        aria-activedescendant={
          showing ? combo.inputProps["aria-activedescendant"] : undefined
        }
        aria-expanded={showing}
        aria-describedby={describedBy}
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onValue(event.target.value);
          setOpen(true);
          combo.setActive(-1);
        }}
        onBlur={close}
        onKeyDown={(event) => {
          fieldKey(event, showing, {
            list: combo.onKeyDown,
            open: () => {
              setOpen(true);
            },
            add,
          });
        }}
      />
      <AddAct name={addName} disabled={typed === ""} onAdd={add} />
      {showing && (
        <ListUnderField
          combo={combo}
          options={options}
          listName={listName}
          typed={typed}
          onTake={take}
        />
      )}
    </div>
  );
}
